import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ProductFeatureName } from 'dashboard/models/organization';
import { SAR, SARStatus } from 'dashboard/models/sar';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/slices/sars';
import { RootState } from 'dashboard/redux/store';
import { loadSARsList } from 'dashboard/redux/thunk-actions/sars';
import DashboardSARsView from 'dashboard/views/DashboardSARsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiModel, ApiSingletonPayload } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { updateDateFilters } from 'lib/utils/filters';

const DashboardSARsRoute = (props: PropsFromRedux & RouteComponentProps) => {
  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.SARsDashboard,
    props.currentEnvironment.type,
  );
  const navigate = useNavigate();

  const { loading: loadingSARs } = useApiLoader({
    loader: () =>
      props.loadSARsList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
      }),
    deps: [
      props.loadSARsList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        switch (error.status) {
          case 401:
            props.showMessage(
              'Your role does not have permissions to view SARs in this organization.',
              MessageType.Notice,
            );
            break;
          default:
            props.showMessage('Failed to load SAR filings.', MessageType.Failure);
        }
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    handlePageLinkClick,
    handleDateUpdate,
    handlePageSizeClick,
    handleOmniSearch,
    handleSortUpdate,
  } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      updateDateFilters(newDateRange, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const { error: createSARError, callback: handleCreateSAR } = useApiCallback(async () => {
    const sar = await SessionClientDashboard.postResourceCreate<
      SAR,
      ApiSingletonPayload<{
        'environment-id': string;
      }>
    >({
      type: ApiModel.SAR,
      attributes: {
        'environment-id': props.currentEnvironment.id,
      },
    });
    props.showMessage('Successfully created a SAR.', MessageType.Success);

    navigate(`/dashboard/sars/${sar.data.id}`);
  }, [props.showMessage, props.currentEnvironment]);

  useApiErrorHandler({
    error: createSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create SAR, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const handleStatusFilterUpdate = (status: SARStatus[]) => {
    props.setFilters({ status });
    props.setCurrentPage(1);
  };

  return (
    <DashboardSARsView
      productFeatureIsDisabled={productFeatureIsDisabled}
      loadingSARs={loadingSARs}
      sars={props.sars}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      handleCreateSAR={handleCreateSAR}
      handleDateUpdate={handleDateUpdateV2}
      handleOmniSearch={handleOmniSearch}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    sars: state.sars.sars[state.currentEnvironment.currentEnvironment.id] || [],
    paginationParams: state.sars.paginationParams,
    sortParams: state.sars.sortParams,
    filters: state.sars.filters,
    currentPage: state.sars.currentPage,
    pageSize: state.sars.pageSize,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  loadSARsList,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardSARsRoute);
